import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Clock, Settings2, Trash2 } from 'lucide-react';

export function ShiftTemplatesDialog({
  open,
  onClose,
  shiftTemplates,
  jobRoles,
  templateFormData,
  setTemplateFormData,
  editingTemplate,
  setEditingTemplate,
  onSave,
  onDelete,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={18} />
            Manage Shift Templates
          </DialogTitle>
          <DialogDescription>
            Create reusable shift templates for faster scheduling.
          </DialogDescription>
        </DialogHeader>

        {/* Template form */}
        <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
          <div className="space-y-1">
            <Label className="text-xs">Template Name</Label>
            <Input
              placeholder="e.g. Morning Shift, Evening Shift"
              value={templateFormData.name}
              onChange={(e) => setTemplateFormData(p => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Start Time</Label>
              <Input
                type="time"
                value={templateFormData.start_time}
                onChange={(e) => setTemplateFormData(p => ({ ...p, start_time: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Time</Label>
              <Input
                type="time"
                value={templateFormData.end_time}
                onChange={(e) => setTemplateFormData(p => ({ ...p, end_time: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Job Role (optional)</Label>
              <Select
                value={templateFormData.role_id}
                onValueChange={(value) => setTemplateFormData(p => ({ ...p, role_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No role</SelectItem>
                  {jobRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                        {role.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Break (mins)</Label>
              <Input
                type="number"
                placeholder="30"
                value={templateFormData.break_duration}
                onChange={(e) => setTemplateFormData(p => ({ ...p, break_duration: e.target.value }))}
              />
            </div>
          </div>
          <Button size="sm" onClick={onSave} className="w-full">
            {editingTemplate ? 'Update Template' : 'Add Template'}
          </Button>
        </div>

        {/* Existing templates */}
        <div className="flex-1 overflow-y-auto mt-3 space-y-2">
          {shiftTemplates.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No templates created yet</p>
          ) : (
            shiftTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-2 border border-slate-200 rounded-lg"
                style={{ borderLeftWidth: '3px', borderLeftColor: template.role_color || template.color || '#94a3b8' }}
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{template.name}</p>
                  <p className="text-xs text-slate-500">
                    {template.start_time} - {template.end_time}
                    {template.break_duration && ` (${template.break_duration}min break)`}
                  </p>
                  {template.role_name && (
                    <p className="text-xs font-medium" style={{ color: template.role_color || '#64748b' }}>
                      {template.role_name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setEditingTemplate(template);
                      setTemplateFormData({
                        name: template.name,
                        role_id: template.role_id || '',
                        start_time: template.start_time,
                        end_time: template.end_time,
                        break_duration: template.break_duration?.toString() || '',
                        label: template.label || '',
                      });
                    }}
                  >
                    <Settings2 size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-600"
                    onClick={() => onDelete(template.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
